/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * RGBARRY-CUSTOMIZATION: post-auth conversion-event flag (W-23955510 / GTM remediation).
 *
 * WHY. `sign_up` and `login` complete in the SERVER action (`_app.signup.tsx` / `_app.login.tsx`),
 * which responds with a redirect — the client hook `useAnalytics()` can't run there. SFRA solved
 * the same problem by driving its GTM "Complete Registration" tag off the post-registration URL
 * (`/account?registration=submitted`); we mirror that exactly: the auth-success terminal appends a
 * query flag to its redirect/navigate target, and a single app-shell consumer
 * (`AuthConversionTracker`) reads the flag once, fires the matching event, and strips the param so
 * a refresh can't re-fire. Same shape as the existing `wishlistMerge` flag (see
 * `src/components/wishlist/wishlist-merge-toast.tsx`).
 *
 * The append is deliberately RELATIVE-SAFE string manipulation (not `new URL`): the login action
 * mutates relative redirect targets in place to keep them relative end-to-end through the
 * open-redirect guard, so this helper must never absolutize a target.
 */

/** Query parameter carrying the completed auth conversion on a post-auth redirect target. */
export const AUTH_CONVERSION_PARAM = 'authEvent';

/** The auth conversions we surface to GA4. Values match the analytics eventType literals. */
export type AuthConversionKind = 'sign_up' | 'login';

const VALID_KINDS: readonly AuthConversionKind[] = ['sign_up', 'login'];

/**
 * Append `?authEvent=<kind>` to a (relative) redirect/navigate target, preserving any existing
 * query string and fragment. Mirrors the `separator = target.includes('?') ? '&' : '?'` idiom the
 * login/OTP flows already use for the `wishlistMerge` flag, so a target can safely carry both.
 */
export function withAuthConversionParam(target: string, kind: AuthConversionKind): string {
    const [pathAndQuery, fragment] = target.split('#');
    const separator = pathAndQuery.includes('?') ? '&' : '?';
    const withParam = `${pathAndQuery}${separator}${AUTH_CONVERSION_PARAM}=${kind}`;
    return fragment !== undefined ? `${withParam}#${fragment}` : withParam;
}

/** Narrow a raw query-param value to a known auth conversion, or null. */
export function parseAuthConversionKind(value: string | null | undefined): AuthConversionKind | null {
    return value && (VALID_KINDS as readonly string[]).includes(value) ? (value as AuthConversionKind) : null;
}
